<template>
  <div
    v-if="props.actions"
    ref="wrapperRef"
    class="action-dots-wrapper"
    @focusout="onWrapperFocusOut"
    @keydown.escape.stop="onEscape"
  >
    <IconButton
      ref="triggerRef"
      :icon="'ri-more-2-fill'"
      :aria-label="toggleButtonAriaLabel"
      :aria-expanded="state.openModal ? 'true' : 'false'"
      aria-haspopup="menu"
      :aria-controls="menuId"
      class="action-dots"
      :data-test-id="testId"
      @click.stop="onTriggerClick"
      @keydown="onTriggerKeydown"
    />
    <!-- Teleport to body so overflow:auto on .table-responsive does not clip the menu (fixed can still be trapped by transformed ancestors). -->
    <Teleport to="body">
      <div
        v-if="state.openModal"
        :id="menuId"
        ref="menuRef"
        class="action-modal action-modal--teleported"
        :style="menuFixedStyle || undefined"
        role="menu"
        :aria-label="menuAriaLabel"
        aria-orientation="vertical"
        @keydown="onMenuKeydown"
      >
        <div
          v-for="(action, index) in actions"
          :key="actionKey(action, index)"
          role="menuitem"
          tabindex="-1"
          class="action action-menuitem"
          data-test-id="action"
          :aria-label="menuItemAriaLabel(action)"
          :aria-posinset="index + 1"
          :aria-setsize="actions.length"
          @click="onMenuItemClick(action)"
          @keydown.enter.prevent="onMenuItemClick(action)"
          @keydown.space.prevent="onMenuItemClick(action)"
        >
          <span :class="'action-icon ' + (action.class || '')">
            <i :class="action.icon" aria-hidden="true"></i>
          </span>
          <span class="action-title">
            <span>{{ action.label }}</span>
          </span>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import {
  computed,
  reactive,
  ref,
  nextTick,
  watch,
  onUnmounted,
  getCurrentInstance
} from "vue"
import { useI18n } from "vue-i18n"
import IconButton from "../Buttons/IconButton.vue"
import { focusWithoutScroll } from "@/helpers/focusWithoutScroll.js"

export default {
  name: "Actions",
  components: { IconButton },
  props: {
    actions: {
      type: Array
    },
    testId: {
      type: String,
      default: ""
    },
    /** Primary row label (e.g. user or room name) so the trigger is unique per row for AT/speech. */
    rowContextLabel: {
      type: String,
      default: ""
    }
  },
  emits: ["clickAction"],
  setup(props, { emit }) {
    const { t } = useI18n()
    const instance = getCurrentInstance()
    const menuId = `table-actions-menu-${instance?.uid ?? Math.random().toString(36).slice(2, 9)}`

    const MENU_VIEW_MARGIN_PX = 8

    const wrapperRef = ref(null)
    const menuRef = ref(null)
    const triggerRef = ref(null)
    /** Fixed viewport positioning so the menu is not clipped by table overflow:auto/hidden ancestors. */
    const menuFixedStyle = ref(null)

    const state = reactive({
      openModal: false
    })

    let repositionRaf = null
    const scheduleRepositionMenu = () => {
      if (!state.openModal) return
      if (repositionRaf != null) cancelAnimationFrame(repositionRaf)
      repositionRaf = requestAnimationFrame(() => {
        repositionRaf = null
        updateMenuPosition()
      })
    }

    const getTriggerElement = () => {
      const inst = triggerRef.value
      return inst?.$el ?? inst
    }

    const updateMenuPosition = () => {
      if (!state.openModal) return
      nextTick(() => {
        requestAnimationFrame(() => {
          if (!state.openModal) return
          const menu = menuRef.value
          const triggerEl = getTriggerElement()
          if (!menu || !triggerEl) return

          const m = MENU_VIEW_MARGIN_PX
          const vw =
            document.documentElement?.clientWidth ?? window.innerWidth ?? 0
          const vh =
            document.documentElement?.clientHeight ?? window.innerHeight ?? 0
          const tr = triggerEl.getBoundingClientRect()

          const mw = menu.offsetWidth || menu.getBoundingClientRect().width
          const mh = menu.offsetHeight || menu.getBoundingClientRect().height

          const gap = 4
          /* Below trigger, right-aligned to ⋮ (kiosk cards + narrow viewports): avoids opening
             to the left of the trigger where the panel clips past the viewport/sidebar. */
          let left = tr.right - mw
          let top = tr.bottom + gap

          if (left < m) left = m
          if (left + mw > vw - m) {
            left = Math.max(m, vw - m - mw)
          }

          if (top + mh > vh - m) {
            top = tr.top - mh - gap
          }
          if (top < m) {
            top = m
          }

          menuFixedStyle.value = {
            position: "fixed",
            left: `${Math.round(left)}px`,
            top: `${Math.round(top)}px`,
            transform: "none",
            right: "auto",
            bottom: "auto",
            zIndex: 10040
          }
        })
      })
    }

    const rowLabelTrimmed = computed(
      () => props.rowContextLabel?.trim?.() || ""
    )

    const toggleButtonAriaLabel = computed(() => {
      if (rowLabelTrimmed.value) {
        return state.openModal
          ? t("common.table_row_actions_close_menu_for", {
              name: rowLabelTrimmed.value
            })
          : t("common.table_row_actions_open_menu_for", {
              name: rowLabelTrimmed.value
            })
      }
      return state.openModal
        ? t("common.table_row_actions_close_menu")
        : t("common.table_row_actions_open_menu")
    })

    const menuAriaLabel = computed(() => {
      if (rowLabelTrimmed.value) {
        return t("common.table_row_actions_menu_for", {
          name: rowLabelTrimmed.value
        })
      }
      return t("common.table_row_actions_menu")
    })

    const menuItemAriaLabel = (action) => {
      if (action?.label?.trim?.()) {
        return undefined
      }
      return t("common.action")
    }

    const actionKey = (action, index) => {
      if (action?.id != null && action.id !== "") return String(action.id)
      if (action?.action != null && action.action !== "")
        return String(action.action)
      return `action-${index}`
    }

    const focusTrigger = () => {
      nextTick(() => {
        const el = triggerRef.value?.$el
        focusWithoutScroll(el)
      })
    }

    const getMenuItems = () =>
      menuRef.value?.querySelectorAll('[role="menuitem"]')

    const focusMenuItem = (index) => {
      const items = getMenuItems()
      if (!items?.length) return
      const len = items.length
      const i = ((index % len) + len) % len
      focusWithoutScroll(items[i])
    }

    const closeMenu = () => {
      if (!state.openModal) return
      state.openModal = false
      focusTrigger()
    }

    const focusFirstMenuItem = () => {
      void nextTick(() => {
        void nextTick(() => {
          focusWithoutScroll(getMenuItems()?.[0])
        })
      })
    }

    const onDocMouseDown = (event) => {
      if (!state.openModal) return
      const t = event?.target
      if (wrapperRef.value?.contains(t)) return
      if (menuRef.value?.contains(t)) return
      state.openModal = false
    }

    watch(
      () => state.openModal,
      (open) => {
        if (open) {
          document.addEventListener("mousedown", onDocMouseDown, true)
          window.addEventListener("resize", scheduleRepositionMenu)
          window.addEventListener("scroll", scheduleRepositionMenu, true)
          void nextTick(() => {
            void nextTick(() => updateMenuPosition())
          })
        } else {
          document.removeEventListener("mousedown", onDocMouseDown, true)
          window.removeEventListener("resize", scheduleRepositionMenu)
          window.removeEventListener("scroll", scheduleRepositionMenu, true)
          menuFixedStyle.value = null
          if (repositionRaf != null) {
            cancelAnimationFrame(repositionRaf)
            repositionRaf = null
          }
        }
      }
    )

    onUnmounted(() => {
      document.removeEventListener("mousedown", onDocMouseDown, true)
      window.removeEventListener("resize", scheduleRepositionMenu)
      window.removeEventListener("scroll", scheduleRepositionMenu, true)
      if (repositionRaf != null) {
        cancelAnimationFrame(repositionRaf)
        repositionRaf = null
      }
    })

    const onWrapperFocusOut = (event) => {
      const wrapper = wrapperRef.value
      const menu = menuRef.value
      if (!wrapper) return
      const next = event.relatedTarget
      if (next && (wrapper.contains(next) || menu?.contains(next))) return
      nextTick(() => {
        if (!state.openModal) return
        const active = document.activeElement
        if (active && (wrapper.contains(active) || menu?.contains(active))) {
          return
        }
        state.openModal = false
      })
    }

    const onEscape = () => {
      if (!state.openModal) return
      state.openModal = false
      nextTick(() => focusTrigger())
    }

    const onTriggerClick = () => {
      state.openModal = !state.openModal
      if (state.openModal) {
        focusFirstMenuItem()
      }
    }

    const onTriggerKeydown = (event) => {
      if (
        (event.key === "ArrowDown" || event.key === "ArrowUp") &&
        !state.openModal
      ) {
        event.preventDefault()
        state.openModal = true
        if (event.key === "ArrowUp") {
          void nextTick(() => {
            void nextTick(() => {
              const items = getMenuItems()
              focusWithoutScroll(items?.[items.length - 1])
            })
          })
        } else {
          focusFirstMenuItem()
        }
      }
    }

    const onMenuKeydown = (event) => {
      if (!state.openModal) return
      const items = getMenuItems()
      if (!items?.length) return

      const current = Array.from(items).indexOf(document.activeElement)

      if (event.key === "ArrowDown") {
        event.preventDefault()
        focusMenuItem(current < 0 ? 0 : current + 1)
        return
      }
      if (event.key === "ArrowUp") {
        event.preventDefault()
        focusMenuItem(current < 0 ? items.length - 1 : current - 1)
        return
      }
      if (event.key === "Home") {
        event.preventDefault()
        focusWithoutScroll(items[0])
        return
      }
      if (event.key === "End") {
        event.preventDefault()
        focusWithoutScroll(items[items.length - 1])
      }
    }

    const onMenuItemClick = (action) => {
      emit("clickAction", action)
      closeMenu()
    }

    return {
      menuId,
      state,
      props,
      wrapperRef,
      menuRef,
      triggerRef,
      menuFixedStyle,
      actionKey,
      toggleButtonAriaLabel,
      menuAriaLabel,
      menuItemAriaLabel,
      onWrapperFocusOut,
      onEscape,
      onTriggerClick,
      onTriggerKeydown,
      onMenuKeydown,
      onMenuItemClick
    }
  }
}
</script>
